/**
 * <s-offer-picker> — Bundle-style quantity cards on the product page.
 *
 * Each card is a radio (name="offer_quantity") whose value is a quantity.
 * The submitted quantity lives in a single hidden input[name="quantity"]
 * inside this element, so the variant picker can read and preserve one value
 * across product-form partial swaps.
 *
 * Required child markup:
 *   input[type="hidden"][name="quantity"] — submitted quantity, source of truth.
 *   input[type="radio"][data-offer-quantity] — one per card.
 */
class OfferPicker extends HTMLElement {
  #onChange = (event) => {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement)) return;
    if (radio.type === 'hidden' && radio.name === 'quantity') {
      this.#syncFromHidden();
      return;
    }
    if (!radio.hasAttribute('data-offer-quantity')) return;
    const hidden = this.#hiddenInput();
    if (!hidden) return;
    hidden.value = radio.value;
    hidden.dispatchEvent(new Event('change', { bubbles: true }));
  };

  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.#syncFromHidden();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  #hiddenInput() {
    const input = this.querySelector('input[type="hidden"][name="quantity"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  #syncFromHidden() {
    const hidden = this.#hiddenInput();
    if (!hidden) return;
    const match = this.querySelector(`input[data-offer-quantity][value="${CSS.escape(hidden.value)}"]`);
    if (match instanceof HTMLInputElement) {
      match.checked = true;
      return;
    }
    const checked = this.querySelector('input[data-offer-quantity]:checked');
    if (checked instanceof HTMLInputElement) hidden.value = checked.value;
  }
}

if (!customElements.get('s-offer-picker')) {
  customElements.define('s-offer-picker', OfferPicker);
}
